import { useState, useEffect } from "react";
import { API, mediaUrl } from "./api";
import MediaBrowser from "./components/MediaBrowser";

const BASE = process.env.REACT_APP_BACKEND_URL;
function downloadUrl(product) {
  if (!product?.zip_file) return "";
  return `${BASE}/api/builder/admin/download/${product.id}`;
}

const EMPTY = {
  name: "", category_id: "", subtitle: "", description: "", account_url: "",
  account_label: "Get your own account", version: "1.0", price: "", edition: "PRO",
  compat: "Joomla 5 & 6", box_image: "", screenshots: [], zip_file: "", tags: [],
  extension_element: "", extension_type: "component", extension_group: "",
  changelog: [],
};

const ENTRY_TYPES = ["feature", "improvement", "fix", "security", "breaking"];
const TYPE_COLORS = {
  feature: "#2563eb", improvement: "#7c3aed", fix: "#16a34a",
  security: "#dc2626", breaking: "#d97706",
};

function Picker({ filter, onClose, onPick }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Select {filter === "zip" ? "ZIP" : filter === "image" ? "image" : "file"}</h3>
          <button className="mb-x" onClick={onClose}>✕</button>
        </div>
        <MediaBrowser filter={filter} onPick={(f) => { onPick(f); onClose(); }} />
      </div>
    </div>
  );
}

function copyDesc(text) {
  navigator.clipboard.writeText(text || "").catch(() => {
    const el = document.createElement("textarea");
    el.value = text || "";
    el.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
    document.body.appendChild(el);
    el.focus(); el.select();
    try { document.execCommand("copy"); } catch (_) {}
    document.body.removeChild(el);
  });
}

// ── Changelog Editor Modal ────────────────────────────────────────────────
function ChangelogEditor({ product, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    API.getChangelog(product.id)
      .then((r) => setEntries(r.changelog || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [product.id]);

  const addEntry = () => setEntries([
    { version: product.version, date: today, type: "improvement", notes: "" },
    ...entries,
  ]);

  const update = (i, k, v) => setEntries(entries.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const remove = (i) => setEntries(entries.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await API.updateChangelog(product.id, entries);
      setMsg("Saved ✓");
    } catch (e) {
      setMsg("Error: " + e.message);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 3000);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="changelog-modal">
      <div className="modal" style={{ maxWidth: 740, width: "96%" }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Changelog — {product.name}</h3>
          <button className="mb-x" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: "0 20px 20px" }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center" }}>
            <button className="btn primary btn-sm" onClick={addEntry} data-testid="add-changelog-entry">+ Add Entry</button>
            <button className="btn primary btn-sm" onClick={save} disabled={saving} data-testid="save-changelog">
              {saving ? "Saving…" : "Save Changelog"}
            </button>
            {msg && <span style={{ fontSize: 13, color: msg.startsWith("Error") ? "#f87171" : "#4ade80" }}>{msg}</span>}
          </div>
          {loading ? <div style={{ padding: 20, textAlign: "center", opacity: .6 }}>Loading…</div> : (
            entries.length === 0
              ? <div style={{ opacity: .5, fontSize: 13, padding: "12px 0" }}>No changelog entries yet. Click "+ Add Entry" to start.</div>
              : entries.map((e, i) => (
                <div key={i} data-testid={`changelog-entry-${i}`} style={{
                  border: `1px solid ${TYPE_COLORS[e.type] || "#334"}`,
                  borderRadius: 8, padding: "12px 14px", marginBottom: 10, background: "#0d1420",
                }}>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 8, alignItems: "center" }}>
                    <input
                      value={e.version} onChange={(ev) => update(i, "version", ev.target.value)}
                      placeholder="1.5.2" style={{ width: 90 }}
                      data-testid={`cl-version-${i}`}
                    />
                    <input
                      type="date" value={e.date} onChange={(ev) => update(i, "date", ev.target.value)}
                      style={{ width: 145 }} data-testid={`cl-date-${i}`}
                    />
                    <select
                      value={e.type} onChange={(ev) => update(i, "type", ev.target.value)}
                      style={{ background: TYPE_COLORS[e.type] || "#334", color: "#fff", border: "none", borderRadius: 4, padding: "4px 8px" }}
                      data-testid={`cl-type-${i}`}
                    >
                      {ENTRY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <button className="link danger" style={{ marginLeft: "auto" }} onClick={() => remove(i)} data-testid={`cl-remove-${i}`}>✕ Remove</button>
                  </div>
                  <textarea
                    rows={3} value={e.notes}
                    onChange={(ev) => update(i, "notes", ev.target.value)}
                    placeholder="What changed in this version? Use plain text or Markdown."
                    style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
                    data-testid={`cl-notes-${i}`}
                  />
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sync Status Banner ────────────────────────────────────────────────────
function SyncBanner({ status, onDismiss }) {
  if (!status) return null;
  const ok = status.success;
  return (
    <div data-testid="sync-banner" style={{
      padding: "10px 14px", borderRadius: 8, marginBottom: 12,
      background: ok ? "#0d1f14" : "#1f0d0d",
      border: `1px solid ${ok ? "#16a34a" : "#dc2626"}`,
      color: ok ? "#4ade80" : "#f87171",
      display: "flex", alignItems: "center", gap: 12, fontSize: 13,
    }}>
      <span>{ok ? "✓" : "✕"} {status.message}</span>
      <button className="link" onClick={onDismiss} style={{ marginLeft: "auto", opacity: .6 }}>dismiss</button>
    </div>
  );
}

export default function Products() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [fCat, setFCat] = useState("");
  const [fTag, setFTag] = useState("");
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [picker, setPicker] = useState(null);
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const [publishMsg, setPublishMsg] = useState("");
  const [publishing, setPublishing] = useState("");
  // sync
  const [syncing, setSyncing] = useState(""); // "push-all" | "pull-all" | pid
  const [syncStatus, setSyncStatus] = useState(null);
  // changelog
  const [changelogFor, setChangelogFor] = useState(null);

  const load = () => {
    API.listProducts(fCat || undefined, fTag || undefined).then(setItems);
    API.listCategories().then(setCats);
    API.listTags().then(setAllTags);
  };
  useEffect(() => { load(); }, [fCat, fTag]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const catName = (id) => cats.find((c) => c.id === id)?.name || "—";

  const publish = async (p) => {
    if (!window.confirm(`Mark "${p.name}" as TESTED and push v${p.version} to the LIVE cart?\n\nClient Joomla sites will receive this version on their next update check.`)) return;
    setPublishing(p.id);
    try {
      const r = await API.publishProduct(p.id);
      setPublishMsg(`✓ Published v${r.version} of "${p.name}" to the live cart — client sites will now update to it.`);
      setTimeout(() => setPublishMsg(""), 5000);
      load();
    } catch (e) {
      setPublishMsg("✕ " + e.message);
      setTimeout(() => setPublishMsg(""), 6000);
    } finally { setPublishing(""); }
  };

  const save = async () => {
    const payload = { ...form, price: form.price === "" ? null : parseFloat(form.price) };
    if (editId) await API.updateProduct(editId, payload);
    else await API.createProduct(payload);
    setForm(null); setEditId(null); load();
  };

  const onPick = (f) => {
    if (picker.target === "box_image") set("box_image", f.url);
    if (picker.target === "zip_file") set("zip_file", f.url);
    if (picker.target === "screenshots") set("screenshots", [...(form.screenshots || []), f.url]);
  };

  // ── Sync helpers ─────────────────────────────────────────────────────────
  const doSync = async (action, pid = null) => {
    const key = pid ? pid : action;
    setSyncing(key);
    setSyncStatus(null);
    try {
      let r;
      if (action === "push-all") r = await API.syncPushAll();
      else if (action === "pull-all") r = await API.syncPullAll();
      else if (action === "push-one") r = await API.syncPushOne(pid);
      else if (action === "pull-one") r = await API.syncPullOne(pid);

      const dir = action.startsWith("pull") ? "← Pull" : "Push →";
      const scope = action.endsWith("all") ? "All" : "1 product";
      setSyncStatus({
        success: true,
        message: `${dir} ${scope} complete — ${r.updated || 0} updated, ${r.inserted || 0} inserted on ${r.live_url || "live server"}.`,
      });
      load();
    } catch (e) {
      setSyncStatus({ success: false, message: e.message });
    } finally {
      setSyncing("");
    }
  };

  // ---------------- FORM ----------------
  if (form) {
    return (
      <div data-testid="product-form">
        <h1>{editId ? "Edit Product" : "New Product"}</h1>
        <div className="panel form-grid">
          <label>Name<input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="prod-name" /></label>
          <label>Category
            <select value={form.category_id} onChange={(e) => set("category_id", e.target.value)} data-testid="prod-category">
              <option value="">— none —</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>Subtitle<input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} /></label>
          <label>Version<input value={form.version} onChange={(e) => set("version", e.target.value)} /></label>
          <label>Edition<input value={form.edition} onChange={(e) => set("edition", e.target.value)} /></label>
          <label>Compatibility<input value={form.compat} onChange={(e) => set("compat", e.target.value)} /></label>
          <label>Extension Element<input value={form.extension_element} onChange={(e) => set("extension_element", e.target.value)} placeholder="e.g. com_myext or idrive" data-testid="field-ext-element" /></label>
          <label>Extension Type
            <select value={form.extension_type} onChange={(e) => set("extension_type", e.target.value)} data-testid="field-ext-type">
              <option value="component">component</option>
              <option value="plugin">plugin</option>
              <option value="module">module</option>
              <option value="template">template</option>
              <option value="package">package</option>
            </select>
          </label>
          <label>Extension Group (plugins only)<input value={form.extension_group} onChange={(e) => set("extension_group", e.target.value)} placeholder="e.g. filesystem, system, user" data-testid="field-ext-group" /></label>
          <label>Price (blank = free)<input value={form.price ?? ""} onChange={(e) => set("price", e.target.value)} /></label>
          <label>Account Button Label<input value={form.account_label} onChange={(e) => set("account_label", e.target.value)} /></label>
          <label className="wide">Tags (comma separated)
            <input value={(form.tags || []).join(", ")} data-testid="prod-tags"
              onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
              placeholder="storage, s3, backup" />
          </label>
          <label className="wide">Account URL<input value={form.account_url} onChange={(e) => set("account_url", e.target.value)} placeholder="https://backblaze.com" /></label>
          <label className="wide">SEO URL<input value={form.seo_url} onChange={(e) => set("seo_url", e.target.value)} placeholder="ai-joomla-security-suite-pro" /></label>
          <label className="wide">Landing Page URL<input value={form.landing_page_url} onChange={(e) => set("landing_page_url", e.target.value)} placeholder="https://aijoomla.org/extensions/..." /></label>
          <label className="wide">Description (full HTML for your cart)
            <textarea rows={10} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="prod-description" />
            <button className="btn ghost" style={{ marginTop: 6 }} type="button"
              onClick={() => { copyDesc(form.description); setCopied("form"); setTimeout(() => setCopied(""), 1500); }}>
              {copied === "form" ? "Copied ✓" : "Copy description"}
            </button>
          </label>

          <div className="wide asset-row">
            <div className="asset">
              <span>Box Image</span>
              {form.box_image ? <img src={mediaUrl(form.box_image)} alt="box" /> : <div className="asset-none">none</div>}
              <button className="btn ghost" onClick={() => setPicker({ filter: "image", target: "box_image" })} data-testid="pick-box">Choose</button>
            </div>
            <div className="asset">
              <span>Install ZIP</span>
              <div className="asset-none">{form.zip_file ? form.zip_file.split("/").pop() : "none"}</div>
              <button className="btn ghost" onClick={() => setPicker({ filter: "zip", target: "zip_file" })} data-testid="pick-zip">Choose</button>
            </div>
            <div className="asset">
              <span>Screenshots ({(form.screenshots || []).length})</span>
              <div className="shots">{(form.screenshots || []).map((s, i) => (
                <span key={i} className="shot"><img src={mediaUrl(s)} alt="" /><button onClick={() => set("screenshots", form.screenshots.filter((_, j) => j !== i))}>✕</button></span>
              ))}</div>
              <button className="btn ghost" onClick={() => setPicker({ filter: "image", target: "screenshots" })}>+ Add</button>
            </div>
          </div>

          <div className="wide actions">
            <button className="btn primary" onClick={save} data-testid="prod-save">{editId ? "Update Product" : "Create Product"}</button>
            <button className="btn ghost" onClick={() => { setForm(null); setEditId(null); }}>Cancel</button>
          </div>
        </div>
        {picker && <Picker filter={picker.filter} onClose={() => setPicker(null)} onPick={onPick} />}
      </div>
    );
  }

  // ---------------- PREVIEW / COPY ----------------
  if (preview) {
    return (
      <div data-testid="product-preview">
        <div className="head-row">
          <h1>{preview.name}</h1>
          <button className="btn ghost" onClick={() => setPreview(null)}>← Back</button>
        </div>
        <div className="panel">
          <div className="asset-row" style={{ marginBottom: 16 }}>
            {preview.box_image && <img src={mediaUrl(preview.box_image)} alt="box" style={{ width: 180, borderRadius: 8 }} />}
            <div>
              <div style={{ marginBottom: 8 }}>{preview.subtitle}</div>
              <div className="tags-row">{(preview.tags || []).map((t) => <span key={t} className="tag">{t}</span>)}</div>
              <div style={{ marginTop: 10, fontSize: 13, opacity: .8 }}>
                {preview.zip_file ? <>ZIP: <a href={mediaUrl(preview.zip_file)} target="_blank" rel="noreferrer">{preview.zip_file.split("/").pop()}</a></> : "no ZIP"}
              </div>
              {preview.extension_element && (
                <div style={{ marginTop: 6, fontSize: 12 }}>
                  <span style={{ opacity: .6 }}>Update feed: </span>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/builder/vendor/updates/${preview.extension_element}.xml`}
                     target="_blank" rel="noreferrer" style={{ fontFamily: "monospace", wordBreak: "break-all" }}>
                    /api/builder/vendor/updates/{preview.extension_element}.xml
                  </a>
                </div>
              )}
            </div>
          </div>
          <div className="actions" style={{ marginBottom: 8 }}>
            {preview.zip_file && (
              <a className="btn primary" href={downloadUrl(preview)} target="_blank" rel="noreferrer" data-testid={`download-preview-${preview.slug}`}>
                ↓ Download {preview.zip_file.split("/").pop()}
              </a>
            )}
            <button className="btn ghost" data-testid="copy-desc-btn"
              onClick={() => { copyDesc(preview.description); setCopied(preview.id); setTimeout(() => setCopied(""), 1500); }}>
              {copied === preview.id ? "Copied to clipboard ✓" : "Copy full description (paste into cart)"}
            </button>
          </div>
          {(preview.screenshots || []).length > 0 && (
            <div className="shots" style={{ margin: "16px 0" }}>
              {preview.screenshots.map((s, i) => <img key={i} src={mediaUrl(s)} alt="" style={{ width: 320, borderRadius: 8, border: "1px solid #223" }} />)}
            </div>
          )}
          <div className="desc-preview panel" style={{ background: "#0d1420" }}
            dangerouslySetInnerHTML={{ __html: preview.description || "<i>No description</i>" }} />
        </div>
      </div>
    );
  }

  // ---------------- LIST ----------------
  return (
    <div data-testid="page-products">
      <div className="head-row">
        <h1>Products</h1>
        <button className="btn primary" onClick={() => { setForm({ ...EMPTY }); setEditId(null); }} data-testid="new-product">+ New Product</button>
      </div>

      {publishMsg && <div className="panel" style={{ borderColor: "#1e4a30", background: "#0d1f14", color: "#4ade80" }} data-testid="publish-msg">{publishMsg}</div>}

      {/* ── Live Server Sync Panel ── */}
      <div className="panel" data-testid="sync-panel" style={{ marginBottom: 16, background: "#0a1628", borderColor: "#1e3a5f" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: "#93c5fd" }}>Live Server Sync</span>
          <span style={{ fontSize: 12, opacity: .6 }}>aijoomla.org</span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              className="btn ghost btn-sm"
              data-testid="sync-push-all"
              disabled={!!syncing}
              onClick={() => doSync("push-all")}
              style={{ borderColor: "#2563eb", color: "#93c5fd" }}
            >
              {syncing === "push-all" ? "Pushing…" : "Push All → Live"}
            </button>
            <button
              className="btn ghost btn-sm"
              data-testid="sync-pull-all"
              disabled={!!syncing}
              onClick={() => {
                if (!window.confirm("Pull ALL products from the live server? This will overwrite local records.")) return;
                doSync("pull-all");
              }}
              style={{ borderColor: "#7c3aed", color: "#c4b5fd" }}
            >
              {syncing === "pull-all" ? "Pulling…" : "← Pull All from Live"}
            </button>
          </div>
        </div>
        <SyncBanner status={syncStatus} onDismiss={() => setSyncStatus(null)} />
      </div>

      <div className="panel filter-bar" data-testid="filter-bar">
        <label>Category
          <select value={fCat} onChange={(e) => setFCat(e.target.value)} data-testid="filter-category">
            <option value="">All</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <div className="tags-row">
          <button className={`chip ${fTag === "" ? "active" : ""}`} onClick={() => setFTag("")} data-testid="tag-all">All tags</button>
          {allTags.map((t) => (
            <button key={t} className={`chip ${fTag === t ? "active" : ""}`} onClick={() => setFTag(fTag === t ? "" : t)} data-testid={`tag-${t}`}>{t}</button>
          ))}
        </div>
      </div>

      <div className="cards">
        {items.length === 0 ? <div className="empty panel">No products match. Adjust filters or create one.</div> :
          items.map((p) => (
            <div className="prod-card" key={p.id} data-testid={`product-${p.slug}`}>
              <div className="prod-thumb">{p.box_image ? <img src={mediaUrl(p.box_image)} alt={p.name} /> : <div className="asset-none">no box</div>}</div>
              <div className="prod-body">
                <div className="prod-title">
                  {p.name} <span className="tag">{p.edition}</span>
                  {p.published && <span className="tag" style={{ background: "#16a34a", color: "#fff", marginLeft: 6 }} data-testid={`live-badge-${p.slug}`}>● LIVE</span>}
                  {p.featured && <span className="tag" style={{ background: "#0e7490", color: "#fff", marginLeft: 6 }}>★ FEATURED</span>}
                </div>
                <div className="prod-cat">{catName(p.category_id)} · v{p.version} · {p.zip_file ? "ZIP ✓" : "no ZIP"}{p.published && p.published_at ? ` · live v${p.extension_version || p.version}` : ""}{(p.changelog || []).length > 0 ? ` · ${p.changelog.length} changelog entries` : ""}</div>
                <div className="prod-sub">{p.subtitle}</div>
                <div className="tags-row">{(p.tags || []).map((t) => <span key={t} className="tag">{t}</span>)}</div>
                <div className="prod-actions">
                  <button className="btn primary btn-sm" disabled={publishing === p.id} onClick={() => publish(p)} data-testid={`publish-${p.slug}`}>
                    {publishing === p.id ? "Publishing…" : (p.published ? "✓ Tested · Re-publish" : "🚀 Mark Tested & Publish")}
                  </button>
                  <button
                    className="btn ghost btn-sm"
                    style={p.featured ? { background: '#0e7490', color: '#fff', border: '1px solid #0e7490' } : {}}
                    data-testid={`feature-toggle-${p.slug}`}
                    onClick={async () => {
                      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/builder/products/${p.id}/featured?featured=${!p.featured}`, { method: 'PATCH' });
                      load();
                    }}
                  >
                    {p.featured ? '★ Unfeature' : '☆ Feature'}
                  </button>
                  {p.zip_file && (
                    <a className="btn ghost btn-sm" href={downloadUrl(p)} target="_blank" rel="noreferrer" data-testid={`download-${p.slug}`}>
                      ↓ Download
                    </a>
                  )}
                  <button className="link" onClick={() => setPreview(p)} data-testid={`view-${p.slug}`}>View / Copy</button>
                  <button className="link" onClick={() => { setForm({ ...EMPTY, ...p }); setEditId(p.id); }}>Edit</button>
                  <button
                    className="link"
                    onClick={() => setChangelogFor(p)}
                    data-testid={`changelog-${p.slug}`}
                    style={{ color: "#93c5fd" }}
                  >
                    Changelog {(p.changelog || []).length > 0 ? `(${p.changelog.length})` : ""}
                  </button>
                  {/* ── Per-product sync ── */}
                  <button
                    className="link"
                    data-testid={`push-one-${p.slug}`}
                    disabled={!!syncing}
                    onClick={() => doSync("push-one", p.id)}
                    style={{ color: "#60a5fa" }}
                  >
                    {syncing === p.id ? "Syncing…" : "Push →"}
                  </button>
                  <button
                    className="link"
                    data-testid={`pull-one-${p.slug}`}
                    disabled={!!syncing}
                    onClick={() => {
                      if (!window.confirm(`Pull "${p.name}" from live? This will overwrite the local record.`)) return;
                      doSync("pull-one", p.id);
                    }}
                    style={{ color: "#a78bfa" }}
                  >
                    ← Pull
                  </button>
                  {confirmDel === p.id ? (
                    <span className="del-confirm" data-testid={`confirm-wrap-${p.slug}`}>
                      <span className="del-q">Delete?</span>
                      <button className="link danger" data-testid={`confirm-delete-${p.slug}`}
                        onClick={async () => { await API.deleteProduct(p.id); setConfirmDel(null); load(); }}>Yes, delete</button>
                      <button className="link" data-testid={`cancel-delete-${p.slug}`}
                        onClick={() => setConfirmDel(null)}>Cancel</button>
                    </span>
                  ) : (
                    <button className="link danger" data-testid={`delete-${p.slug}`}
                      onClick={() => setConfirmDel(p.id)}>Delete</button>
                  )}
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* ── Changelog Modal ── */}
      {changelogFor && (
        <ChangelogEditor product={changelogFor} onClose={() => { setChangelogFor(null); load(); }} />
      )}
    </div>
  );
}
